import ReturnsDataTableToolbar from "@/pages/(app)/returns/list/components/returns-data-table/returns-data-table-toolbar";
import useReturnRequestsTable from "@/pages/(app)/returns/list/hooks/use-return-requests-table";
import { Card, CardContent } from "@babackapp/ui/components/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@babackapp/ui/components/table";
import { flexRender } from "@tanstack/react-table";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useFilteredReturnRequests } from "../../hooks/use-filtered-return-requests";
import useTranslation from "@babackapp/translations/use-translation";
import { ReturnRequestsPagination } from "./return-requests-pagination";
import { Skeleton } from "@babackapp/ui/components/skeleton";

export default function ReturnsDataTable() {
  const navigate = useNavigate();

  const t = useTranslation();
  const { isPending, data } = useFilteredReturnRequests();

  const table = useReturnRequestsTable({
    returnRequests: data?.data ?? [],
    isLoading: isPending,
  });

  const hasMore = data?.hasMore;

  const renderSkeletonRows = () => {
    return Array(5)
      .fill(0)
      .map((_, index) => (
        <TableRow key={`skeleton-${index}`} className="group">
          {table.getAllColumns().map((column) => (
            <TableCell
              key={`skeleton-cell-${column.id}`}
              className="first:pl-6 h-16"
              style={{
                width: column.getSize(),
                minWidth: 200,
              }}
            >
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ));
  };

  return (
    <div className="flex flex-col gap-4 min-w-0">
      <Card className="flex-1 min-w-0">
        <CardContent className="py-1 px-0 rounded-t-md min-w-0">
          <div className="flex-grow min-w-0">
            <div className="px-4 py-4 sticky top-0 bg-white z-50 border-b border-b-border rounded-t-md">
              <ReturnsDataTableToolbar table={table} />
            </div>
            <div className="relative h-full overflow-x-auto min-w-0">
              <Table className="sticky top-0 w-full table-fixed">
                <TableHeader className="px-6 bg-muted">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const sortingIcon = header.column.getIsSorted() ? (
                          header.column.getIsSorted() === "asc" ? (
                            <ChevronUp className="inline ml-2 w-4 h-4" />
                          ) : (
                            <ChevronDown className="inline ml-2 w-4 h-4" />
                          )
                        ) : null;
                        return (
                          <TableHead
                            key={header.id}
                            className={`first:pl-6 h-12 select-none ${
                              header.column.getCanSort() ? "cursor-pointer" : ""
                            }`}
                            colSpan={header.colSpan}
                            style={{
                              width: header.column.getSize(),
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {header.isPlaceholder ? null : (
                              <>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                                {sortingIcon}
                              </>
                            )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {isPending ? (
                    renderSkeletonRows()
                  ) : table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        className="group"
                        onClick={() => {
                          if (
                            !isPending &&
                            row.original?.shopId &&
                            row.original?.id
                          ) {
                            navigate(
                              `/shops/${row.original.shopId}/returns/${row.original.id}`,
                              {
                                state: {
                                  fromList: true,
                                },
                              },
                            );
                          }
                        }}
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className="first:pl-6 h-16 cursor-pointer"
                            style={{
                              width: cell.column.getSize(),
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell className="h-24 text-center">
                        {t("misc.noResultFound")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
      <ReturnRequestsPagination canGoNext={hasMore} />
    </div>
  );
}
